
import React, { useEffect, useState } from 'react';
import { useWedding } from '../context/WeddingContext';
import { getTranslation } from '../i18n';
import { getAuthenticatedSupabase } from '../supabaseClient';
import { filterVisibleInGuests } from '../lib/guests/capacity';
import { Users, CheckCircle, Wallet, Calendar } from 'lucide-react';
import { Link, useParams } from 'react-router-dom';
import { aggregateBudget } from '../lib/budget';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';

export default function Dashboard() {
  const { wedding, locale } = useWedding();
  const { weddingId } = useParams();
  const t = (key: string) => getTranslation(locale, key);

  const [stats, setStats] = useState({ guests: 0, confirmed: 0, tasks: 0, budget: 0, paid: 0 });
  const [tasks, setTasks] = useState<any[]>([]);

  useEffect(() => {
    if (!wedding) return;
    const fetch = async () => {
      const supabase = getAuthenticatedSupabase();
      const [g, t, budgetAgg] = await Promise.all([
        supabase.from('guests').select('*').eq('wedding_id', wedding.id),
        supabase.from('tasks').select('*').eq('wedding_id', wedding.id).neq('status', 'done').order('due_date').limit(3),
        aggregateBudget(wedding.id)
      ]);
      const guestList = g.data || [];
      const visible = filterVisibleInGuests(guestList);
      const confirmedCount = visible.filter((x: any) => x.rsvp_status === 'confirmed').length;

      setStats({
        guests: visible.length,
        confirmed: confirmedCount,
        tasks: t.data?.length || 0,
        budget: budgetAgg.totalPlanned,
        paid: budgetAgg.totalPaid
      });
      setTasks(t.data || []);
    };
    fetch();
  }, [wedding]);

  if (!wedding) return null;

  const weddingDate = new Date(wedding.date);
  const diff = weddingDate.getTime() - new Date().getTime();
  const diffDays = Math.ceil(diff / (1000 * 60 * 60 * 24));
  
  const baseUrl = `/app/w/${weddingId}`;

  return (
    <div className="space-y-10 animate-fade-in">
      {/* Welcome Hero */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <Card className="lg:col-span-2 flex flex-col justify-between p-8">
          <div className="space-y-4">
            <h1 className="text-ds-h1 text-slate-900 dark:text-white">Vítejte zpět</h1>
            <p className="text-ds-body text-slate-500 dark:text-slate-400 max-w-lg leading-relaxed">
              Vaše svatba v <span className="font-semibold text-slate-700 dark:text-slate-300">{wedding.location}</span> se blíží. Máte hotovo 
              <span className="font-semibold text-slate-700 dark:text-slate-300"> {stats.confirmed} z {stats.guests}</span> odpovědí od hostů.
            </p>
          </div>
          <div className="flex flex-wrap gap-3 mt-8">
            <Link to={`${baseUrl}/guests`}>
              <Button>Pozvat hosty</Button>
            </Link>
            <Link to={`${baseUrl}/tasks`}>
              <Button variant="ghost">Zobrazit úkoly</Button>
            </Link>
          </div>
        </Card>

        <Card className="flex flex-col items-center justify-center text-center p-8">
           <Calendar size={24} className="mb-4 text-brand-500" strokeWidth={1.5} />
           <p className="text-ds-meta text-slate-500 dark:text-slate-400 mb-2">Zbývá</p>
           <h3 className="text-5xl font-medium text-slate-900 dark:text-white mb-1">{diffDays > 0 ? diffDays : '0'}</h3>
           <p className="text-ds-meta text-slate-500 dark:text-slate-400">{t('common.days_to_go')}</p>
        </Card>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="p-6">
          <div className="flex items-center gap-3 mb-5">
            <Users size={18} className="text-slate-400" strokeWidth={1.5} />
            <span className="text-ds-meta text-slate-500">Hosté</span>
          </div>
          <div className="space-y-1">
            <h4 className="text-ds-h2 text-slate-900 dark:text-white font-medium">
              {stats.confirmed} <span className="text-slate-400 font-normal">/ {stats.guests}</span>
            </h4>
            <p className="text-ds-meta text-slate-400">Potvrzené odpovědi</p>
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center gap-3 mb-5">
            <CheckCircle size={18} className="text-slate-400" strokeWidth={1.5} />
            <span className="text-ds-meta text-slate-500">Úkoly</span>
          </div>
          <div className="space-y-1">
            <h4 className="text-ds-h2 text-slate-900 dark:text-white font-medium">{stats.tasks}</h4>
            <p className="text-ds-meta text-slate-400">Aktivní úkoly k řešení</p>
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center gap-3 mb-5">
            <Wallet size={18} className="text-slate-400" strokeWidth={1.5} />
            <span className="text-ds-meta text-slate-500">Rozpočet</span>
          </div>
          <div className="space-y-1">
            <h4 className="text-ds-h2 text-slate-900 dark:text-white font-medium">
              {Math.round(stats.paid / 1000)}k <span className="text-slate-400 font-normal">/ {Math.round(stats.budget / 1000)}k</span>
            </h4>
            <p className="text-ds-meta text-slate-400">{t('budget.paid_czk')}</p>
          </div>
        </Card>
      </div>
    </div>
  );
}
